"use client";

import {
	Building2,
	Home,
	RefreshCw,
	AlertTriangle,
	ServerCrash,
	WifiOff,
	ShieldAlert,
	MapPin,
	LogOut,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Link from "next/link";
import { signOutUser } from "@/services/cognito";
import { isAuthenticatedSync } from "@/lib/auth";

type ErrorType =
	| "404"
	| "500"
	| "network"
	| "401"
	| "geolocation"
	| "location_permission_denied"
	| "server_connection";

interface ErrorPageProps {
	type: ErrorType;
	reset?: () => void;
}

export default function ErrorPage({ type, reset }: ErrorPageProps) {
	const router = useRouter();
	const [isSigningOut, setIsSigningOut] = useState(false);
	const isLoggedIn = isAuthenticatedSync();

	const errorConfigs = {
		"404": {
			icon: AlertTriangle,
			title: "404 - Page Not Found",
			subtitle: "Oops! The property you're looking for doesn't exist",
			description:
				"The page you are trying to access may have been moved, deleted, or never existed. Let's get you back on track.",
			color: "#F59E0B",
			bgColor: "#FEF3C7",
			action: "Go Back Home",
		},
		"500": {
			icon: ServerCrash,
			title: "500 - Server Error",
			subtitle: "Something went wrong on our end",
			description:
				"Our servers are experiencing issues. Our team has been notified and we're working to fix this as quickly as possible. Please try again in a few moments.",
			color: "#EF4444",
			bgColor: "#FEE2E2",
			action: "Refresh Page",
		},
		network: {
			icon: WifiOff,
			title: "Network Connection Lost",
			subtitle: "Unable to connect to Tax Lien Vest",
			description:
				"It looks like you're offline or experiencing network issues. Please check your internet connection and try again.",
			color: "#6B7280",
			bgColor: "#F3F4F6",
			action: "Try Again",
		},
		"401": {
			icon: ShieldAlert,
			title: "401 - Unauthorized Access",
			subtitle: "You don’t have permission to view this",
			description:
				"The page you're trying to access requires authentication or you don’t have the necessary permissions. Please log in or contact support if you believe this is an error.",
			color: "#8B5CF6",
			bgColor: "#EDE9FE",
			action: "Sign In",
		},
		geolocation: {
			icon: MapPin,
			title: "Unable to Fetch Your Location",
			subtitle: "Location access is unavailable",
			description:
				"We are unable to fetch your live location. This could be due to location permissions being denied, location services being disabled, or a timeout. Please try searching for a location manually using the search bar.",
			color: "#F59E0B",
			bgColor: "#FEF3C7",
			action: "Search Manually",
		},
		location_permission_denied: {
			icon: MapPin,
			title: "Location Permission Denied",
			subtitle: "Enable location access to find nearby properties",
			description:
				"Your browser has blocked location access for this site. To enable it:",
			steps: [
				"Click the lock icon (🔒) or information icon (ℹ️) in your browser's address bar",
				"Find 'Location' in the permissions list",
				"Change it from 'Block' to 'Allow'",
				"Refresh this page and try again",
			],
			alternativeText:
				"Alternatively, you can search for a location manually using the search bar above.",
			color: "#F59E0B",
			bgColor: "#FEF3C7",
			action: "Try Again",
		},
		server_connection: {
			icon: ServerCrash,
			title: "Server Connection Refused",
			subtitle: "Unable to connect with Tax Lien server",
			description:
				"Our server might be temporarily unreachable or your connection is being blocked. Please check your internet connection and try again.",
			color: "#EF4444",
			bgColor: "#FEE2E2",
			action: "Try Again",
		},
	};

	const config = errorConfigs[type];
	const Icon = config.icon;

	const handleAction = () => {
		if (typeof window === "undefined") return;

		switch (type) {
			case "404":
				router.push("/");
				break;
			case "500":
				if (reset) {
					reset();
				} else {
					window.location.reload();
				}
				break;
			case "network":
				window.location.reload();
				break;
			case "401":
				router.push("/login");
				break;
			case "geolocation":
				// For geolocation error, call reset to allow manual search
				// The reset function will clear the error state
				if (reset) {
					reset();
				}
				break;
			case "location_permission_denied":
				// Reload page to retry after user enables permission
				window.location.reload();
				break;
			case "server_connection":
				window.location.reload();
				break;
		}
	};

	const handleSignOut = async () => {
		try {
			setIsSigningOut(true);
			await signOutUser();
			router.push("/login");
		} catch (error) {
			console.error("Sign out error:", error);
			// Even if sign out fails, redirect to login
			router.push("/login");
		} finally {
			setIsSigningOut(false);
		}
	};

	return (
		<div className="min-h-screen bg-[#16251c] flex flex-col items-center justify-center p-3 md:p-4 lg:p-6 py-8 md:py-12">
			{/* Main Content */}
			<div
				className="w-full max-w-[340px] md:max-w-[60%] lg:max-w-[75%] flex flex-col"
				style={{ alignItems: "center", justifyContent: "center" }}
			>
				{/* Header */}
				<div className="text-center mb-2 md:mb-4 lg:mb-6">
					<div className="flex items-center justify-center gap-2 mb-2 md:mb-3 lg:mb-4">
						<div className="w-7 h-7 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full bg-[#A7FF5E] flex items-center justify-center">
							<Building2 className="h-4 w-4 md:h-6 md:w-6 lg:h-7 lg:w-7 text-[#16251c]" />
						</div>
						<span className="text-[#edffde] text-base md:text-xl lg:text-2xl tracking-wide">
							Tax Lien Vest
						</span>
					</div>
				</div>

				{/* Error Card */}
				<div
					className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 lg:p-8 xl:p-12 text-center w-full min-h-[280px] md:min-h-0"
					style={{ maxWidth: "80%" }}
				>
					{/* Icon */}
					<div
						className="inline-flex items-center justify-center w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full mb-2 md:mb-3 lg:mb-4"
						style={{ backgroundColor: config.bgColor }}
					>
						<Icon
							className="h-7 w-7 md:h-10 md:w-10 lg:h-12 lg:w-12"
							style={{ color: config.color }}
						/>
					</div>

					{/* Title */}
					<h3 className="text-lg md:text-2xl lg:text-3xl mb-1.5 md:mb-2 lg:mb-3 text-[#16251b] font-bold">
						{config.title}
					</h3>

					{/* Subtitle */}
					<p
						className="text-sm md:text-lg lg:text-xl mb-2 md:mb-2.5 lg:mb-3 font-semibold"
						style={{ color: config.color }}
					>
						{config.subtitle}
					</p>

					{/* Description */}
					<div className="text-gray-600 mb-4 md:mb-5 lg:mb-6 max-w-md mx-auto leading-relaxed text-xs md:text-sm lg:text-base text-left px-1">
						<p className="mb-3">{config.description}</p>
						{"steps" in config && config.steps && (
							<ol className="list-decimal list-inside space-y-2 mb-3 ml-2">
								{config.steps.map((step, index) => (
									<li key={index} className="pl-2">
										{step}
									</li>
								))}
							</ol>
						)}
						{"alternativeText" in config &&
							config.alternativeText && (
								<p className="mt-3">{config.alternativeText}</p>
							)}
					</div>

					{/* Actions */}
					<div className="flex flex-col sm:flex-row gap-2 md:gap-3 justify-center items-center">
						<Button
							onClick={handleAction}
							className="bg-[#A7FF5E] text-[#16251c] hover:bg-[#95e84d] h-9 md:h-10 lg:h-11 px-4 md:px-5 lg:px-6 text-xs md:text-sm lg:text-base w-full sm:w-auto"
						>
							{type === "500" || type === "network" ? (
								<>
									<RefreshCw className="h-3 w-3 md:h-4 md:w-4 mr-2" />
									{config.action}
								</>
							) : (
								config.action
							)}
						</Button>

						<Link href="/" className="w-full sm:w-auto">
							<Button
								variant="outline"
								className="h-9 md:h-10 lg:h-11 px-4 md:px-5 lg:px-6 border-gray-300 text-[#16251c] hover:bg-gray-50 text-xs md:text-sm lg:text-base w-full sm:w-auto"
							>
								<Home className="h-3 w-3 md:h-4 md:w-4 mr-2" />
								Back to Home
							</Button>
						</Link>

						{isLoggedIn && (
							<Button
								onClick={handleSignOut}
								disabled={isSigningOut}
								variant="outline"
								className="h-9 md:h-10 lg:h-11 px-4 md:px-5 lg:px-6 border-gray-300 text-[#16251c] hover:bg-gray-50 text-xs md:text-sm lg:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<LogOut className="h-3 w-3 md:h-4 md:w-4 mr-2" />
								{isSigningOut ? "Signing out..." : "Sign Out"}
							</Button>
						)}
					</div>

					{/* Additional Help */}
					<div className="mt-3 md:mt-4 lg:mt-6 pt-3 md:pt-4 lg:pt-6 border-t border-gray-200">
						<p className="text-xs md:text-sm text-gray-500">
							Need help?{" "}
							<Link
								href="/contact-us"
								className="text-[#417714] hover:underline"
							>
								Contact Support
							</Link>
						</p>
					</div>
				</div>

				{/* Footer Note */}
				<div className="text-center mt-3 md:mt-4 lg:mt-6">
					<p className="text-xs text-[#edffde]/60">
						Error Code: {type.toUpperCase()} • Tax Lien Vest
						Platform
					</p>
				</div>
			</div>
		</div>
	);
}
